/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-argument */
import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import dayjs from 'dayjs'
import qs from 'qs'
import { deserialize } from 'ts-jackson'
import { FacilityBookingMembership } from '../facility/model'
import { PageInfo, PaginationOpt, PaginationResponse } from '../model'
import {
  BookingClassPayload,
  CancelClassBookingPayload,
  ClassBooking,
  ClassBookingCancelPreview,
  ClassBookingQuote,
  ClassBookingPaymentMethod,
  ClassCategory,
  ClassInfo,
  CreateRecurringClassSchedulePayload,
  MemberSchedule,
  Schedule,
  ScheduleSpots,
  TrainerWithAvailableTime,
} from './model'

/** Largest pageSize the /classes/schedules endpoint accepts. */
const SCHEDULE_MAX_PAGE_SIZE = 500

export const ClassService = (client: AxiosInstance) => {
  return {
    async getClassSchedule(
      opt: PaginationOpt & { startTime: string; endTime: string }
    ): Promise<PaginationResponse<Schedule>> {
      const pageSize = opt.pageSize ?? SCHEDULE_MAX_PAGE_SIZE
      const fetchPage = async (page: number) => {
        const { data } = await wrapApiCall(() =>
          client.get('/classes/schedules', { params: { ...opt, page, pageSize } })
        )
        return data
      }

      const first = await fetchPage(opt.page ?? 1)
      const items: Record<string, unknown>[] = first?.data ?? []

      // Callers (calendar + export) need the whole date range at once, but the
      // endpoint paginates — a busy month easily exceeds one page. When no
      // specific page was requested, follow the rest so no schedule is dropped.
      if (opt.page === undefined) {
        const total: number = first?.pagination?.total ?? items.length
        const pageCount = Math.ceil(total / pageSize)
        const rest = await Promise.all(
          Array.from({ length: Math.max(pageCount - 1, 0) }, (_, index) => fetchPage(index + 2))
        )
        rest.forEach((page) => items.push(...(page?.data ?? [])))
      }

      return {
        pagination: deserialize(first?.pagination, PageInfo),
        data: items.map((item) => deserialize(item, Schedule)),
      }
    },
    async getClassScheduleById(
      id: string | number,
      opt: PaginationOpt & { status: string }
    ): Promise<PaginationResponse<Schedule>> {
      const { data } = await wrapApiCall(() =>
        client.get(`/classes/${id}/schedules`, {
          params: { ...opt, status: opt.status.toLowerCase() },
        })
      )
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) => deserialize(item, Schedule)),
      }
    },
    async getClassScheduleMemberById(
      classId: string | number,
      scheduleId: string | number
    ): Promise<MemberSchedule[]> {
      const { data } = await wrapApiCall(() =>
        client.get(`/classes/${classId}/${scheduleId}/members`)
      )
      return data?.map((item: Record<string, unknown>) => deserialize(item, MemberSchedule))
    },
    async uploadImageToClass(id: string | number, file: File): Promise<boolean> {
      const formData = new FormData()
      formData.append('image', file)

      const { data } = await wrapApiCall(() =>
        client.post(`/classes/${id}/images`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
      )
      return !!data?.success
    },
    async removeImageFromClass(id: string | number, imageId: string | number): Promise<boolean> {
      const { data } = await wrapApiCall(() => client.delete(`/classes/${id}/images/${imageId}`))
      return !!data?.success
    },
    async getClassById(id: string | number): Promise<ClassInfo> {
      const { data } = await wrapApiCall(() => client.get(`/classes/${id}`))
      return deserialize(data, ClassInfo)
    },
    async getClasses(opt: PaginationOpt): Promise<PaginationResponse<ClassInfo>> {
      const { data } = await wrapApiCall(() => client.get('/classes', { params: opt }))
      return {
        pagination: deserialize(data?.pagination, PageInfo),
        data: data?.data?.map((item: Record<string, unknown>) => deserialize(item, ClassInfo)),
      }
    },
    async deleteClass(id: string | number): Promise<void> {
      await wrapApiCall(() => client.delete(`/classes/${id}`))
    },
    async getScheduleById(
      classId: string | number,
      scheduleId: string | number
    ): Promise<Schedule> {
      const { data } = await wrapApiCall(() =>
        client.get(`/classes/${classId}/schedules/${scheduleId}`)
      )
      return deserialize(data, Schedule)
    },
    /**
     * Headcount behind the cancel confirm dialog (QTY-2085): how many members
     * the cancel is about to notify, split booked / waiting list. Read-only —
     * calling it cancels nothing.
     *
     * Returns null when the payload is not three numbers. Defaulting the
     * missing ones to 0 would be worse than showing nothing: the dialog would
     * state "No one is booked on this class — nobody will be notified" over a
     * class with thirty people on it, and it says that directly above a
     * destructive button. An unreadable count must read as unknown, never as
     * zero.
     */
    async getCancelImpact(
      id: string | number,
      scheduleId: string | number
    ): Promise<ClassCancelImpact | null> {
      const { data } = await wrapApiCall(() =>
        client.get(`/classes/${id}/schedules/${scheduleId}/cancel-impact`)
      )
      const raw = data as Partial<Record<keyof ClassCancelImpact, unknown>> | null
      const count = (v: unknown): number | null =>
        typeof v === 'number' && Number.isFinite(v) && v >= 0 ? v : null

      const booked = count(raw?.booked)
      const waitingList = count(raw?.waitingList)
      const notifiable = count(raw?.notifiable)
      if (booked === null || waitingList === null || notifiable === null) {
        // Say so. The caller treats this exactly like a network failure — it
        // drops the numbers from the dialog — so without a line here a changed
        // response shape would look identical to a flaky connection and go
        // unnoticed for as long as it took someone to wonder why the counts
        // stopped appearing.
        console.warn('[Class] Unexpected cancel-impact payload', data)
        return null
      }

      return { booked, waitingList, notifiable }
    },
    async cancelSchedule(id: string | number, scheduleId: string | number): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.patch(`/classes/${id}/schedules/${scheduleId}/cancel`)
      )
      return !!data?.success
    },
    async deleteSchedule(id: string | number, scheduleId: string | number): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.delete(`/classes/${id}/schedules/${scheduleId}`)
      )
      return !!data?.success
    },
    async deleteScheduleByGroupId(id: string | number, groupId: string | number): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.delete(`/classes/${id}/schedules-group/${groupId}`)
      )
      return !!data?.success
    },
    async toggleClassStatus(id: string | number, flag: boolean): Promise<ClassInfo> {
      const { data: responseData } = await wrapApiCall(() =>
        client.patch(`/classes/${id}/status`, { isActive: flag })
      )
      return deserialize(responseData, ClassInfo)
    },
    async getClassCategory(): Promise<ClassCategory[]> {
      const { data } = await wrapApiCall(() => client.get('/classes/categories'))
      return data?.map((item: Record<string, unknown>) => deserialize(item, ClassCategory))
    },
    async createClass(data: ClassInfo): Promise<ClassInfo> {
      const { data: responseData } = await wrapApiCall(() =>
        client.post('/classes', data.serializeToCreate())
      )
      return deserialize(responseData, ClassInfo)
    },
    async updateClass(id: string | number, data: ClassInfo): Promise<ClassInfo> {
      const { data: responseData } = await wrapApiCall(() =>
        client.patch(`/classes/${id}`, data.serializeToCreate())
      )
      return deserialize(responseData, ClassInfo)
    },
    async createClassSchedule(classId: string | number, data: Schedule): Promise<Schedule> {
      const { data: responseData } = await wrapApiCall(() =>
        client.post(`/classes/${classId}/schedules`, data.serializeToCreate())
      )
      return deserialize(responseData, Schedule)
    },
    async editClassSchedule(
      classId: string | number,
      scheduleId: string | number,
      data: Schedule
    ): Promise<Schedule> {
      const { data: responseData } = await wrapApiCall(() =>
        client.patch(`/classes/${classId}/schedules/${scheduleId}`, data.serializeToCreate())
      )
      return deserialize(responseData, Schedule)
    },
    async createScheduleWithBooking(classId: string | number, data: Schedule): Promise<Schedule> {
      const { data: responseData } = await wrapApiCall(() =>
        client.post(
          `/classes/${classId}/schedules/with-booking`,
          data.serializeToCreateWithBooking()
        )
      )
      return deserialize(responseData, Schedule)
    },
    async getAvailableTrainersBySchedule(body: {
      scheduleId: string | number
      startTime: string
      endTime: string
      roleIds: number[]
    }): Promise<TrainerWithAvailableTime[]> {
      const { scheduleId, ...params } = body
      const { data } = await wrapApiCall(() =>
        client.get(`/classes/schedules/${scheduleId}/available-trainers`, {
          params,
          paramsSerializer: (p) => qs.stringify(p, { arrayFormat: 'repeat' }),
        })
      )
      return data?.map((item: Record<string, unknown>) =>
        deserialize(item, TrainerWithAvailableTime)
      )
    },
    async getAvailableReScheduleLists(
      bookingId: string | number,
      date: string
    ): Promise<Schedule[]> {
      const { data } = await wrapApiCall(() =>
        client.get(`/bookings/${bookingId}/available-reschedule-list`, {
          params: { startDate: date },
        })
      )
      return data?.map((item: Record<string, unknown>) => deserialize(item, Schedule))
    },
    async rescheduleBooking(
      bookingId: string | number,
      scheduleId: string | number
    ): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.patch(`/bookings/${bookingId}/schedule`, { scheduleId })
      )
      return !!data?.success
    },
    async changeTrainers(scheduleId: string | number, trainerIds: number[]): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.patch(`/classes/schedules/${scheduleId}/trainers`, { trainerIds })
      )
      return !!data.success
    },
    async cancelBooking(
      bookingId: string | number,
      opts?: { branchIdOverride?: number } & CancelClassBookingPayload
    ): Promise<boolean> {
      // Branch-scoped on the BE; cross-branch lists (member class history)
      // pass the booking's own branch so the row doesn't 404.
      const config =
        opts?.branchIdOverride != null
          ? { headers: { 'branch-id': String(opts.branchIdOverride) } }
          : undefined
      // Only staff may send `refund`, and only a deliberate choice should reach
      // the wire: an empty body keeps the backend's own default (refund) and
      // keeps every pre-existing caller behaving exactly as before.
      const body: CancelClassBookingPayload = {
        ...(typeof opts?.refund === 'boolean' ? { refund: opts.refund } : {}),
        ...(opts?.reason?.trim() ? { reason: opts.reason.trim() } : {}),
      }
      const { data } = await wrapApiCall(() =>
        client.patch(
          `/bookings/${bookingId}/cancel`,
          Object.keys(body).length ? body : undefined,
          config
        )
      )
      return !!data.success
    },
    /**
     * What cancelling this booking would do TO THE MEMBER — the deadline,
     * whether it is already late, and what the club's late-cancel policy takes
     * from them. Staff see the member's consequence so the confirm dialog can
     * state it before someone presses cancel on their behalf.
     */
    async getCancelPreview(
      bookingId: string | number,
      opts?: { branchIdOverride?: number }
    ): Promise<ClassBookingCancelPreview> {
      const config =
        opts?.branchIdOverride != null
          ? { headers: { 'branch-id': String(opts.branchIdOverride) } }
          : undefined
      const { data } = await wrapApiCall(() =>
        client.get(`/bookings/${bookingId}/cancel-preview`, config)
      )
      return deserialize(data as Record<string, unknown>, ClassBookingCancelPreview)
    },
    /**
     * "Teach it anyway": exempts one round from the minimum-participants sweep
     * (or puts it back under the rule). PATCH
     * /classes/:id/schedules/:scheduleId/keep-below-minimum.
     */
    async setKeepBelowMinimum(
      classId: string | number,
      scheduleId: string | number,
      keep: boolean
    ): Promise<Schedule> {
      const { data } = await wrapApiCall(() =>
        client.patch(`/classes/${classId}/schedules/${scheduleId}/keep-below-minimum`, { keep })
      )
      return deserialize(data, Schedule)
    },
    async bookingClass(data: BookingClassPayload): Promise<ClassBooking> {
      const { data: responseData } = await wrapApiCall(() => client.post('/bookings', data))
      return deserialize(responseData, ClassBooking)
    },

    async walkInBookingClass(data: BookingClassPayload): Promise<ClassBooking> {
      const { data: responseData } = await wrapApiCall(() => client.post('/bookings/walk-in', data))
      return deserialize(responseData, ClassBooking)
    },
    /**
     * What `POST /bookings` will do for this member on this schedule —
     * participant type, charge, the package it would spend, and whether it can
     * go through at all. Same auth as `find-membership`, which it replaces on
     * the staff "add member" modal: that one only answered "which package", so
     * the modal had to guess the participant type from the member's global
     * classification and could disagree with the backend.
     *
     * A refusal is not an error here: the endpoint answers 200 with
     * `canBook: false` and a `blockReason`.
     */
    async getBookingQuote(params: {
      userId: number | string
      scheduleId: string | number
    }): Promise<ClassBookingQuote> {
      const { data } = await wrapApiCall(() => client.get('/bookings/quote', { params }))
      return deserialize(data as Record<string, unknown>, ClassBookingQuote)
    },
    /**
     * The seat map for one round: every cell of the room's grid plus who is
     * sitting there. A room without a map answers `requiresSpotSelection:
     * false` with no spots, which is how the seat-map surfaces decide whether
     * to render at all.
     */
    async getScheduleSpots(
      classId: string | number,
      scheduleId: string | number
    ): Promise<ScheduleSpots> {
      const { data } = await wrapApiCall(() =>
        client.get(`/classes/${classId}/schedules/${scheduleId}/spots`)
      )
      return deserialize(data as Record<string, unknown>, ScheduleSpots)
    },
    /**
     * Moves a booking to another seat. Staff may do this any time before the
     * class starts; 409 means somebody took that seat in the meantime, so the
     * caller refetches the grid rather than retrying blind.
     */
    async updateBookingSpot(
      bookingId: string | number,
      payload: { spotId: string }
    ): Promise<ClassBooking> {
      const { data } = await wrapApiCall(() => client.patch(`/bookings/${bookingId}/spot`, payload))
      return deserialize(data as Record<string, unknown>, ClassBooking)
    },
    /**
     * Staff verdict on a member-uploaded transfer slip.
     * approve → the booking becomes PAID (TRANSFER) and a receipt is issued.
     * reject  → REJECTED; the member may upload another slip and the 15-minute
     * seat hold is re-armed. PATCH /bookings/:id/slip.
     */
    async updateBookingSlip(
      bookingId: string | number,
      payload: { approve: boolean }
    ): Promise<ClassBooking> {
      const { data } = await wrapApiCall(() => client.patch(`/bookings/${bookingId}/slip`, payload))
      return deserialize(data as Record<string, unknown>, ClassBooking)
    },
    /**
     * Staff records a counter payment for a PAID class booking (cash / card /
     * transfer) → PAID + receipt. PATCH /bookings/:id/mark-paid.
     */
    async markBookingPaid(
      bookingId: string | number,
      payload: { method: ClassBookingPaymentMethod }
    ): Promise<ClassBooking> {
      const { data } = await wrapApiCall(() =>
        client.patch(`/bookings/${bookingId}/mark-paid`, payload)
      )
      return deserialize(data as Record<string, unknown>, ClassBooking)
    },
    /**
     * The receipt issued when this class booking was settled — null when there is
     * none (free/session/credit bookings, or rows settled before class bookings
     * issued receipts). GET /bookings/:id/receipt; used to print the standard PDF.
     */
    async getBookingReceipt(
      bookingId: string | number
    ): Promise<{ id: number; code: string } | null> {
      const { data } = await wrapApiCall(() =>
        client.get<{ receipt: { id: number; code: string } | null }>(
          `/bookings/${bookingId}/receipt`
        )
      )
      return data?.receipt ?? null
    },
    async findClassBookingMembership(params: {
      userId: number
      scheduleId: string | number
    }): Promise<FacilityBookingMembership> {
      const { data } = await wrapApiCall(() => client.get('/bookings/find-membership', { params }))
      return deserialize(data as Record<string, unknown>, FacilityBookingMembership)
    },
    async checkIn(
      bookingId: string | number,
      opts?: { branchIdOverride?: number }
    ): Promise<boolean> {
      const config =
        opts?.branchIdOverride != null
          ? { headers: { 'branch-id': String(opts.branchIdOverride) } }
          : undefined
      const { data } = await wrapApiCall(() =>
        client.post(`/bookings/${bookingId}/check-in`, undefined, config)
      )
      return !!data?.success
    },
    async removeCheckIn(
      bookingId: string | number,
      opts?: { branchIdOverride?: number }
    ): Promise<boolean> {
      const config =
        opts?.branchIdOverride != null
          ? { headers: { 'branch-id': String(opts.branchIdOverride) } }
          : undefined
      const { data } = await wrapApiCall(() =>
        client.delete(`/bookings/${bookingId}/check-in`, config)
      )
      return !!data?.success
    },
    async createRecurringClassSchedules(
      classId: string | number,
      data: CreateRecurringClassSchedulePayload
    ): Promise<Schedule[]> {
      const { data: responseData } = await wrapApiCall(() =>
        client.post(`/classes/${classId}/schedules/recurring`, data)
      )
      const items = Array.isArray(responseData) ? responseData : (responseData?.data ?? [])
      return (items as Record<string, unknown>[]).map((item) => deserialize(item, Schedule))
    },
    async previewRecurringClassSchedules(
      classId: string | number,
      data: CreateRecurringClassSchedulePayload
    ): Promise<{ count: number; dates: string[] }> {
      const { data: responseData } = await wrapApiCall(() =>
        client.post(`/classes/${classId}/schedules/recurring/preview`, data)
      )
      const raw = responseData as { count?: number; dates?: string[] }
      return {
        count: raw?.count ?? 0,
        dates: raw?.dates ?? [],
      }
    },
    async editFutureClassSchedules(
      classId: string | number,
      scheduleId: string | number,
      data: Schedule
    ): Promise<{ updatedCount: number }> {
      const { data: responseData } = await wrapApiCall(() =>
        client.patch(`/classes/${classId}/schedules/${scheduleId}/future`, data.serializeToCreate())
      )
      return responseData as { updatedCount: number }
    },
    async deleteFutureClassSchedules(
      classId: string | number,
      scheduleId: string | number
    ): Promise<boolean> {
      const { data } = await wrapApiCall(() =>
        client.delete(`/classes/${classId}/schedules/${scheduleId}/future`)
      )
      return !!data?.success
    },
    async getFutureSessionsImpact(
      classId: string | number,
      scheduleId: string | number
    ): Promise<FutureSessionsImpact> {
      const { data } = await wrapApiCall(() =>
        client.get(`/classes/${classId}/schedules/${scheduleId}/future-impact`)
      )
      const raw = data as {
        sessions?: Array<{
          scheduleId: number
          startTime: string
          endTime: string
          bookingCount: number
        }>
        sessionCount: number
        totalBookings: number
        members?: string[]
      }
      return {
        sessionCount: raw.sessionCount,
        totalBookings: raw.totalBookings,
        members: raw.members,
        sessions: raw.sessions?.map((s) => ({
          scheduleId: s.scheduleId,
          date: dayjs(s.startTime).format('D MMM YYYY'),
          startTime: dayjs(s.startTime).format('HH:mm'),
          endTime: dayjs(s.endTime).format('HH:mm'),
          bookingCount: s.bookingCount,
        })),
      }
    },
  }
}

/** Response of `GET /classes/:id/schedules/:scheduleId/cancel-impact`. */
export type ClassCancelImpact = {
  /** Members holding a confirmed place. */
  booked: number
  /** Members on the waiting list. */
  waitingList: number
  /** Of those, how many have a LINE account linked and will actually be pushed. */
  notifiable: number
}

export type FutureSessionsImpactSession = {
  scheduleId: number
  date: string
  startTime: string
  endTime: string
  bookingCount: number
}

export type FutureSessionsImpact = {
  sessionCount: number
  totalBookings: number
  members?: string[]
  sessions?: FutureSessionsImpactSession[]
}
